import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dataPolicyPath } from '../../views/policy/dataPolicyPath'
import { Stack } from '@mui/material'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useConsentMutation, useGetUsersConsent } from '../../api/ester-b2b-api/private/consent/consentQueries'
import { ConsentId } from '../../api/ester-b2b-api/private/consent/types'
import { theme } from '../../theme/theme'
import { BodyMedium } from '../styles/Typography'
import { Checkbox } from '../inputs/Checkbox'

/**
 * Generic consent hook that can be used for any consent type
 * @param consentId - The ID of the consent to manage
 * @returns Consent data, loading state, checkbox state, and helper functions
 */
export const useConsent = (consentId: ConsentId) => {
  const { data: consentData, isLoading: isConsentLoading } = useGetUsersConsent(consentId)
  const { mutate: setUserConsent } = useConsentMutation()
  const [consentCheckbox, setConsentCheckbox] = useState(false)

  return {
    consentData,
    isConsentLoading,
    consentCheckbox,
    setConsentCheckbox,
    setUserConsent,
    isButtonDisabled: !consentData && !consentCheckbox,
  }
}

/**
 * Screening-specific consent hook
 * Wrapper around useConsent for screening consent
 */
export const useScreeningConsent = () => {
  return useConsent(ConsentId.SCREENING_CONSENT)
}

interface ScreeningConsentCheckboxProps {
  consentCheckbox: boolean
  setConsentCheckbox: (value: boolean | ((prev: boolean) => boolean)) => void
}

export const ScreeningConsentCheckbox = ({ consentCheckbox, setConsentCheckbox }: ScreeningConsentCheckboxProps) => {
  const { t, i18n } = useTranslation()

  return (
    <Stack direction="row">
      <Checkbox
        onChange={() => setConsentCheckbox((pre) => !pre)}
        checked={consentCheckbox}
        borderColor={theme.color.grey3}
        backgroundColor={theme.color.white}
      />
      <ConsentTextContainer>
        <BodyMedium>
          {t('employee.screening.consentAgree')}{' '}
          <Link to={dataPolicyPath(i18n.language, '#3')} target="_blank" rel="noopener noreferrer">
            {t('employee.screening.dataProcessing')}
          </Link>
          {t('employee.screening.consentWithdraw')}
        </BodyMedium>
      </ConsentTextContainer>
    </Stack>
  )
}

const ConsentTextContainer = styled.div`
  margin-left: ${theme.spacing.small}px;
  text-align: left;
  & a {
    color: ${theme.color.plumMid};
    text-decoration: underline;
  }
  & p {
    color: ${theme.color.grey4};
  }
`
